import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOHead from "../components/shared/SEOHead.jsx";
import JsonLd from "../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd } from "../utils/jsonld.js";
import { INFO_FAQ } from "./infoFaq.js";
import { SOCIAL_LINKS } from "../components/shared/socialLinks.jsx";

const socialIconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "38px",
  height: "38px",
  borderRadius: "999px",
  background: "rgba(10,9,7,0.06)",
  color: "var(--color-ink)",
};

const INFO_PATH = { de: "infos", en: "info" };
const KONTAKT_PATH = { de: "kontakt", en: "contact" };
const PRESS_PATH = { de: "presse", en: "press" };
const ABOUT_PATH = { de: "ueber-uns", en: "about" };
const CHARTER_PATH = { de: "gesellschaftsvertrag", en: "charter" };
const REPORTS_PATH = { de: "rechenschaftsberichte", en: "accountability-reports" };

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  padding: "12px 0",
  borderBottom: "1px solid rgba(254,250,239,0.1)",
  fontSize: "14px",
};

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ background: "var(--color-bg-raised)", borderRadius: "var(--radius-card)", padding: "24px 26px" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          margin: 0,
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--color-cream)",
        }}
      >
        <span style={{ fontSize: "16px" }}>{question}</span>
        <span style={{ fontSize: "18px", color: open ? "var(--color-gold)" : "rgba(254,250,239,0.5)", flex: "none" }}>{open ? "−" : "+"}</span>
      </button>
      {open &&
        answer.split("\n\n").map((paragraph, i) => (
          <p key={i} style={{ fontSize: "14px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: i === 0 ? "14px 0 0" : "10px 0 0" }}>
            {paragraph}
          </p>
        ))}
    </div>
  );
}

/** Real org facts only — the contact card itself has no phone number, but see infoFaq.js for the fund-usage/receipt details covered in the FAQ below. */
export default function Info({ lang }) {
  const { t } = useTranslation();
  const path = `/${INFO_PATH[lang]}`;
  const altPath = `/${INFO_PATH[lang === "de" ? "en" : "de"]}`;
  const faq = INFO_FAQ[lang];

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={t("info.title")} />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("info.title"), path },
          ],
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", padding: "64px 40px 96px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "54ch" }}>
            <p className="tl-eyebrow" style={{ margin: 0 }}>
              {t("info.eyebrow")}
            </p>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
              {t("info.title")}
            </h1>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "32px" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)" }}>{t("info.contactTitle")}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "15px", lineHeight: 1.6, color: "rgba(10,9,7,0.75)" }}>
                <div>
                  Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt)
                  <br />
                  Reiboldschachtring 28
                  <br />
                  01705 Freital
                </div>
                <div>
                  <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
                </div>
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.key}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    title={social.label}
                    style={socialIconButtonStyle}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "4px" }}>
                <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark">
                  {t("info.contactCta")}
                </Link>
                <Link to={`/${lang}/${PRESS_PATH[lang]}`} className="tl-pill tl-pill-ghost-dark">
                  {t("info.pressCta")}
                </Link>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px", background: "var(--color-bg-raised)", borderRadius: "var(--radius-card)", padding: "32px" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-cream)" }}>{t("info.transparencyTitle")}</span>
              <div>
                <div style={rowStyle}>
                  <span style={{ color: "var(--color-cream-soft)" }}>{t("info.legalForm")}</span>
                  <span style={{ color: "var(--color-cream)", textAlign: "right" }}>gUG (haftungsbeschränkt), gemeinnützig</span>
                </div>
                <div style={rowStyle}>
                  <span style={{ color: "var(--color-cream-soft)" }}>{t("info.register")}</span>
                  <span style={{ color: "var(--color-cream)", textAlign: "right" }}>Amtsgericht Dresden, HRB 48120</span>
                </div>
                <div style={rowStyle}>
                  <span style={{ color: "var(--color-cream-soft)" }}>{t("info.nonprofitStatus")}</span>
                  <span style={{ color: "var(--color-cream)", textAlign: "right" }}>Finanzamt Pirna, 09.06.2026 (§ 60a AO)</span>
                </div>
                <div style={rowStyle}>
                  <span style={{ color: "var(--color-cream-soft)" }}>{t("info.charterRow")}</span>
                  <Link to={`/${lang}/${ABOUT_PATH[lang]}/${CHARTER_PATH[lang]}`} style={{ color: "var(--color-gold)", textAlign: "right" }}>
                    {t("info.viewLink")}
                  </Link>
                </div>
                <div style={{ ...rowStyle, borderBottom: "none" }}>
                  <span style={{ color: "var(--color-cream-soft)" }}>{t("info.reportsRow")}</span>
                  <Link to={`/${lang}/${ABOUT_PATH[lang]}/${REPORTS_PATH[lang]}`} style={{ color: "var(--color-gold)", textAlign: "right" }}>
                    {t("info.viewLink")}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {faq?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
                {t("info.faqTitle")}
              </h2>
              {faq.map(({ category, items }) => (
                <div key={category} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px", color: "var(--color-gold)" }}>
                    {category}
                  </span>
                  {items.map(({ question, answer }) => (
                    <FaqItem key={question} question={question} answer={answer} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export { INFO_PATH };
